import React, { useState, useEffect } from 'react'
import { getAccessToken } from '../utils/tokenStorage'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Avatar,
  Tooltip,
  Switch,
  FormControlLabel,
  CircularProgress,
  Divider,
  Rating
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Assessment as AssessmentIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Star as StarIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  LockReset as LockResetIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material'
import { userService } from '../services/api'
import { 
  UserFilters, 
  UserWithProfile, 
  CreateUserRequest,
  UpdateUserRequest,
  Tenant,
  Institution
} from '../types/api'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'

// Componenta pentru dialog-ul de creare/editare utilizator
interface UserDialogProps {
  open: boolean
  onClose: () => void
  user?: UserWithProfile
  onSave: (userData: CreateUserRequest | UpdateUserRequest) => void
  loading: boolean
}

const UserDialog: React.FC<UserDialogProps> = ({ open, onClose, user, onSave, loading }) => {
  const { user: currentUser } = useAuth() // Obține utilizatorul curent pentru a verifica rolul
  
  const [formData, setFormData] = useState<CreateUserRequest | UpdateUserRequest>({
    email: user?.email || '',
    password: '',
    role: user?.role || 'medic',
    phone: user?.phone || '',
    active: user?.active ?? true,
    tenant_id: user?.tenant_id || (currentUser?.tenant_id || '') // Setează automat tenant_id pentru operatori
  })
  
  const [availableTenants, setAvailableTenants] = useState<Tenant[]>([])
  const [loadingTenants, setLoadingTenants] = useState(false)
  
  // State pentru instituții
  const [availableInstitutions, setAvailableInstitutions] = useState<Institution[]>([])
  const [selectedInstitutions, setSelectedInstitutions] = useState<Institution[]>([])
  const [loadingInstitutions, setLoadingInstitutions] = useState(false)
  
  // ✨ State pentru instituții coordonate (doar pentru medici)
  const [coordinatedInstitutionIds, setCoordinatedInstitutionIds] = useState<string[]>([])

  // Încarcă tenant-urile disponibile (doar pentru admin)
  const loadAvailableTenants = async () => {
    // Operatorii nu au nevoie să vadă tenant-urile - folosesc automat propriul tenant
    if (currentUser?.role === 'operator') {
      // Pentru operatori, setăm automat tenant_id din currentUser
      if (!user && currentUser.tenant_id) {
        setFormData(prev => ({ ...prev, tenant_id: currentUser.tenant_id }))
      }
      return
    }
    
    try {
      setLoadingTenants(true)
      const tenants = await userService.getAvailableTenants()
      setAvailableTenants(tenants)
      
      // Setează automat primul tenant pentru utilizatori noi (doar pentru admin)
      if (!user && tenants.length > 0 && !formData.tenant_id) {
        setFormData(prev => ({ ...prev, tenant_id: tenants[0].id }))
      }
    } catch (error) {
      console.error('Eroare la încărcarea tenant-urilor:', error)
      toast.error('Eroare la încărcarea tenant-urilor')
    } finally {
      setLoadingTenants(false)
    }
  }

  // Încarcă instituțiile disponibile pentru tenant-ul selectat
  const loadAvailableInstitutions = async (tenantId: string) => {
    if (!tenantId) return
    
    try {
      setLoadingInstitutions(true)
      const institutions = await userService.getInstitutionsForTenant(tenantId)
      setAvailableInstitutions(institutions)
    } catch (error) {
      console.error('Eroare la încărcarea instituțiilor:', error)
      toast.error('Eroare la încărcarea instituțiilor')
    } finally {
      setLoadingInstitutions(false)
    }
  }

  // Încarcă instituțiile utilizatorului existent
  const loadUserInstitutions = async (userId: string) => {
    try {
      const institutions = await userService.getUserInstitutions(userId)
      setSelectedInstitutions(institutions)
    } catch (error) {
      console.error('Eroare la încărcarea instituțiilor utilizatorului:', error)
      toast.error('Eroare la încărcarea instituțiilor utilizatorului')
    }
  }

  // ✨ Încarcă instituțiile coordonate ale utilizatorului
  const loadUserCoordinatedInstitutions = async (userId: string) => {
    try {
      const response = await fetch(`/api/v1/users/${userId}/coordinated-institutions`, {
        headers: {
          'Authorization': `Bearer ${getAccessToken()}`,
          'Content-Type': 'application/json'
        }
      })
      if (response.ok) {
        const data = await response.json()
        setCoordinatedInstitutionIds((data || []).map((inst: any) => inst.id))
      }
    } catch (error) {
      console.error('Eroare la încărcarea instituțiilor coordonate:', error)
    }
  }

  // Adaugă o instituție la lista selectată
  const addInstitution = (institution: Institution) => {
    if (!selectedInstitutions.find(inst => inst.id === institution.id)) {
      setSelectedInstitutions(prev => [...prev, institution])
    }
  }

  // Șterge o instituție din lista selectată
  const removeInstitution = (institutionId: string) => {
    setSelectedInstitutions(prev => prev.filter(inst => inst.id !== institutionId))
  }

  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    cod_parafa: '',
    phone: '',
    modalities: [] as string[]
  })

  useEffect(() => {
    // Încarcă tenant-urile când se deschide dialog-ul
    if (open) {
      loadAvailableTenants()
    }
    
    if (user) {
      setFormData({
        email: user.email,
        role: user.role || 'medic',
        phone: user.phone || '',
        active: user.active,
        tenant_id: user.tenant_id || ''
      })

      // Încarcă instituțiile pentru tenant-ul utilizatorului
      if (user.tenant_id) {
        loadAvailableInstitutions(user.tenant_id)
        loadUserInstitutions(user.id)
        // ✨ Încarcă și instituțiile coordonate pentru medici
        if (user.role === 'medic') {
          loadUserCoordinatedInstitutions(user.id)
        }
      }

      // Populează datele de profil în funcție de rol
      if (user.doctor_profile) {
        setProfileData({
          first_name: user.doctor_profile.first_name || '',
          last_name: user.doctor_profile.last_name || '',
          cod_parafa: user.doctor_profile.cod_parafa || '',
          phone: user.phone || '',
          modalities: user.doctor_profile.modalities || []
        })
      } else if (user.operator_profile) {
        setProfileData({
          first_name: user.operator_profile.first_name || '',
          last_name: user.operator_profile.last_name || '',
          cod_parafa: '',
          phone: user.operator_profile.phone || '',
          modalities: []
        })
      } else if (user.tehnician_profile) {
        setProfileData({
          first_name: user.tehnician_profile.first_name || '',
          last_name: user.tehnician_profile.last_name || '',
          cod_parafa: '',
          phone: user.tehnician_profile.phone || '',
          modalities: []
        })
      } else {
        // Pentru utilizatori fără profil (admin sau utilizatori noi)
        setProfileData({
          first_name: '',
          last_name: '',
          cod_parafa: '',
          phone: user.phone || '',
          modalities: []
        })
      }
    } else {
      // Reset pentru utilizator nou
      setFormData({
        email: '',
        password: '',
        role: 'medic',
        phone: '',
        active: true,
        tenant_id: currentUser?.tenant_id || '' // Setează automat tenant_id pentru operatori
      })
      setProfileData({
        first_name: '',
        last_name: '',
        cod_parafa: '',
        phone: '',
        modalities: []
      })
      // Reset instituții pentru utilizator nou
      setAvailableInstitutions([])
      setSelectedInstitutions([])
      setCoordinatedInstitutionIds([]) // ✨ Reset și coordonator
    }
  }, [user, open]) // Adăugat 'open' ca dependință pentru a re-popula când se deschide dialog-ul

  // Monitorizează schimbarea tenant_id și încarcă instituțiile automat
  useEffect(() => {
    if (formData.tenant_id && open) {
      loadAvailableInstitutions(formData.tenant_id)
    }
  }, [formData.tenant_id, open])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // Verifică dacă este utilizator nou și are parolă
    if (!user) {
      // Pentru utilizatori noi, formData este de tip CreateUserRequest
      const createData = formData as CreateUserRequest
      if (!createData.password || createData.password.trim() === '') {
        toast.error('Parola este obligatorie pentru utilizatori noi')
        return
      }
    }
    
    // Combină datele de bază cu datele de profil și instituțiile
    const combinedData = {
      ...formData,
      profile: {
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        cod_parafa: profileData.cod_parafa,
        phone: profileData.phone,
        modalities: profileData.modalities
      },
      institutions: selectedInstitutions.map(inst => inst.id),
      // ✨ Adaugă instituțiile coordonate (doar pentru medici)
      coordinated_institutions: formData.role === 'medic' ? coordinatedInstitutionIds : []
    }
    
    onSave(combinedData)
  }

  const availableModalities = ['CT', 'MR', 'DX', 'US', 'XA', 'NM', 'PT', 'CR', 'MG', 'RF']

  // Dialog compact styling
  const dialogPaperSx = {
    bgcolor: 'background.paper',
    backgroundImage: 'none',
    '& .MuiInputLabel-root': { fontSize: '0.8rem' },
    '& .MuiOutlinedInput-root': { fontSize: '0.85rem' },
    '& .MuiMenuItem-root': { fontSize: '0.8rem' },
  }

  return (
    <Dialog 
      open={open} 
      onClose={(_, reason) => { if (reason !== 'backdropClick') onClose() }}
      disableEscapeKeyDown
      maxWidth="md" 
      fullWidth
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle sx={{ 
        py: 1.5, 
        px: 2,
        fontSize: '1rem',
        fontWeight: 600,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        {user ? 'Editare Utilizator' : 'Utilizator Nou'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 2 }}>
          <Grid container spacing={1.5}>
            {/* Rând 1: Nume, Prenume */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Nume"
                value={profileData.last_name}
                onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Prenume"
                value={profileData.first_name}
                onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                required
              />
            </Grid>

            {/* Rând 2: Email, Rol */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                disabled={!!user}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Rol</InputLabel>
                <Select
                  value={formData.role || ''}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  label="Rol"
                >
                  <MenuItem value="admin">Administrator</MenuItem>
                  <MenuItem value="operator">Operator</MenuItem>
                  <MenuItem value="medic">Medic</MenuItem>
                  <MenuItem value="tehnician">Tehnician</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Rând 3: Cod parafă (doar medic), Telefon */}
            {formData.role === 'medic' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Cod parafă"
                  value={profileData.cod_parafa}
                  onChange={(e) => setProfileData({ ...profileData, cod_parafa: e.target.value })}
                  required
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Telefon"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>

            {/* Rând 4: Parolă (doar la creare), Modalități (doar medic) */}
            {!user && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Parolă"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                />
              </Grid>
            )}
            {formData.role === 'medic' && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Modalități</InputLabel>
                  <Select
                    multiple
                    value={profileData.modalities}
                    onChange={(e) => setProfileData({ 
                      ...profileData, 
                      modalities: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value 
                    })}
                    label="Modalități"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" sx={{ height: 20, fontSize: '0.72rem' }} />
                        ))}
                      </Box>
                    )}
                  >
                    {availableModalities.map((modality) => (
                      <MenuItem key={modality} value={modality}>
                        {modality}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Secțiunea Instituții - la final */}
            {availableInstitutions.length > 0 && (
              <Grid item xs={12} sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ 
                  fontWeight: 600, 
                  color: 'primary.main',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  mb: 1,
                  display: 'block'
                }}>
                  Instituții
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {availableInstitutions.map((institution) => {
                    const isSelected = selectedInstitutions.find(inst => inst.id === institution.id)
                    return (
                      <Chip
                        key={institution.id}
                        label={institution.name}
                        onClick={() => isSelected ? removeInstitution(institution.id) : addInstitution(institution)}
                        color={isSelected ? "primary" : "default"}
                        variant={isSelected ? "filled" : "outlined"}
                        size="small"
                        sx={{ 
                          cursor: 'pointer',
                          height: 24,
                          fontSize: '0.75rem'
                        }}
                      />
                    )
                  })}
                </Box>
              </Grid>
            )}

            {loadingInstitutions && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Se încarcă instituțiile...
                </Typography>
              </Grid>
            )}

            {/* ✨ Secțiunea Coordonator - doar pentru medici, afișează instituțiile selectate */}
            {formData.role === 'medic' && selectedInstitutions.length > 0 && (
              <Grid item xs={12} sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ 
                  fontWeight: 600, 
                  color: 'success.main',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  mb: 1,
                  display: 'block'
                }}>
                  Coordonator
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
                  Selectează instituțiile pentru care medicul va fi coordonator (vede toate studiile)
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selectedInstitutions.map((institution) => {
                    const isCoordinator = coordinatedInstitutionIds.includes(institution.id)
                    return (
                      <Chip
                        key={`coord-${institution.id}`}
                        label={institution.name}
                        onClick={() => {
                          if (isCoordinator) {
                            setCoordinatedInstitutionIds(prev => prev.filter(id => id !== institution.id))
                          } else {
                            setCoordinatedInstitutionIds(prev => [...prev, institution.id])
                          }
                        }}
                        color={isCoordinator ? "success" : "default"}
                        variant={isCoordinator ? "filled" : "outlined"}
                        size="small"
                        sx={{ 
                          cursor: 'pointer',
                          height: 24,
                          fontSize: '0.75rem'
                        }}
                      />
                    )
                  })}
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', justifyContent: 'space-between' }}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                size="small"
              />
            }
            label={<Typography sx={{ fontSize: '0.78rem' }}>Activ</Typography>}
            sx={{ ml: 0 }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} disabled={loading} size="small" sx={{ fontSize: '0.8rem' }}>
              Anulează
            </Button>
            <Button type="submit" variant="contained" disabled={loading} size="small" sx={{ fontSize: '0.8rem' }}>
              {loading ? <CircularProgress size={16} /> : (user ? 'Salvează' : 'Creează')}
            </Button>
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  )
}

// Componenta pentru dialog-ul de scoring
interface ScoringDialogProps {
  open: boolean
  onClose: () => void
  user?: UserWithProfile
  score?: any // UserScore type was removed, so using 'any' for now
  loading: boolean
}

const ScoringDialog: React.FC<ScoringDialogProps> = ({ open, onClose, user, score, loading }) => {
  // Eliminăm variabilele neutilizate
  // const [dateRange, setDateRange] = useState({
  //   from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  //   to: new Date().toISOString().split('T')[0]
  // })

  // useEffect(() => {
  //   if (open && user) {
  //     // Încarcă scorul pentru perioada selectată
  //   }
  // }, [open, user])

  if (!user || !score) return null

  // Eliminăm funcția neutilizată
  // const getScoreColor = (score: number) => {
  //   if (score >= 4.5) return 'success'
  //   if (score >= 3.5) return 'warning'
  //   return 'error'
  // }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Scoring și KPI - {user.first_name} {user.last_name}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <StarIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">
                      Scor curent: {score.current_score}
                    </Typography>
                    <Rating 
                      value={parseFloat(score.current_score)} 
                      readOnly 
                      precision={0.1}
                      sx={{ ml: 2 }}
                    />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Perioada: {score.period.from} - {score.period.to}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Studii finalizate
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {score.metrics.studies_completed}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Studii urgente
                  </Typography>
                  <Typography variant="h4" color="error">
                    {score.metrics.urgent_studies}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Timp mediu preluare (min)
                  </Typography>
                  <Typography variant="h4" color="info">
                    {Math.round(score.metrics.avg_pickup_time / 60)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Timp mediu finalizare (min)
                  </Typography>
                  <Typography variant="h4" color="secondary">
                    {Math.round(score.metrics.avg_finalize_time / 60)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Închide
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// Componenta pentru dialog-ul de resetare parolă de către admin
interface ResetPasswordDialogProps {
  open: boolean
  onClose: () => void
  user: UserWithProfile | undefined
  onReset: (userId: string, autoGenerate: boolean, manualPassword?: string) => Promise<void>
  loading: boolean
}

const ResetPasswordDialog: React.FC<ResetPasswordDialogProps> = ({ 
  open, 
  onClose, 
  user, 
  onReset, 
  loading 
}) => {
  const [resetMode, setResetMode] = useState<'auto' | 'manual'>('auto')
  const [manualPassword, setManualPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [passwordErrors, setPasswordErrors] = useState<string[]>([])

  // Reset state când se închide dialogul
  useEffect(() => {
    if (!open) {
      setResetMode('auto')
      setManualPassword('')
      setConfirmPassword('')
      setShowPassword(false)
      setPasswordErrors([])
    }
  }, [open])

  // Validare parolă
  useEffect(() => {
    if (resetMode === 'manual' && manualPassword) {
      const errors: string[] = []
      
      if (manualPassword.length < 8) {
        errors.push('Minimum 8 caractere')
      }
      if (!/[a-z]/.test(manualPassword)) {
        errors.push('Cel puțin o literă mică')
      }
      if (!/[A-Z]/.test(manualPassword)) {
        errors.push('Cel puțin o literă mare')
      }
      if (!/\d/.test(manualPassword)) {
        errors.push('Cel puțin o cifră')
      }
      
      setPasswordErrors(errors)
    } else {
      setPasswordErrors([])
    }
  }, [manualPassword, resetMode])

  const handleSubmit = async () => {
    if (resetMode === 'manual') {
      if (passwordErrors.length > 0) {
        toast.error('Parola nu îndeplinește cerințele de securitate')
        return
      }
      if (manualPassword !== confirmPassword) {
        toast.error('Parolele nu coincid')
        return
      }
    }

    if (!user) return

    await onReset(
      user.id, 
      resetMode === 'auto',
      resetMode === 'manual' ? manualPassword : undefined
    )
  }

  // Dialog compact styling
  const dialogPaperSx = {
    bgcolor: 'background.paper',
    backgroundImage: 'none',
    '& .MuiInputLabel-root': { fontSize: '0.8rem' },
    '& .MuiOutlinedInput-root': { fontSize: '0.85rem' },
    '& .MuiMenuItem-root': { fontSize: '0.8rem' },
  }

  return (
    <Dialog 
      open={open} 
      onClose={(_, reason) => { if (reason !== 'backdropClick') onClose() }}
      disableEscapeKeyDown
      maxWidth="sm" 
      fullWidth
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle sx={{ 
        py: 1.5, 
        px: 2,
        fontSize: '1rem',
        fontWeight: 600,
        borderBottom: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <LockResetIcon sx={{ fontSize: 20 }} />
        Resetare Parolă
      </DialogTitle>
      
      <DialogContent sx={{ p: 2 }}>
        <Box>
          <Typography sx={{ fontSize: '0.82rem', mb: 1.5 }} color="text.secondary">
            Utilizator: <strong>{user?.email}</strong>
          </Typography>
          
          <FormControl fullWidth size="small">
            <InputLabel>Mod resetare</InputLabel>
            <Select
              value={resetMode}
              onChange={(e) => setResetMode(e.target.value as 'auto' | 'manual')}
              disabled={loading}
            >
              <MenuItem value="auto">Generare automată</MenuItem>
              <MenuItem value="manual">Setare manuală</MenuItem>
            </Select>
          </FormControl>

          {resetMode === 'auto' && (
            <Box sx={{ 
              mt: 1.5, 
              p: 1.5, 
              bgcolor: 'info.dark',
              color: 'info.contrastText',
              borderRadius: 1,
              fontSize: '0.78rem'
            }}>
              ✓ Va fi generată automat o parolă securizată<br/>
              ✓ Parola va fi afișată pentru copiere
            </Box>
          )}

          {resetMode === 'manual' && (
            <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                label="Parolă nouă"
                type={showPassword ? 'text' : 'password'}
                value={manualPassword}
                onChange={(e) => setManualPassword(e.target.value)}
                disabled={loading}
                error={passwordErrors.length > 0}
                helperText={passwordErrors.length > 0 ? passwordErrors.join(', ') : ''}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
                    </IconButton>
                  )
                }}
              />

              <TextField
                fullWidth
                size="small"
                label="Confirmă parola"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                error={confirmPassword !== '' && manualPassword !== confirmPassword}
                helperText={
                  confirmPassword !== '' && manualPassword !== confirmPassword 
                    ? 'Parolele nu coincid' 
                    : ''
                }
              />

              <Box sx={{ 
                mt: 1, 
                p: 1, 
                bgcolor: 'warning.dark',
                color: 'warning.contrastText',
                borderRadius: 1,
                fontSize: '0.72rem'
              }}>
                Cerințe: min. 8 caractere, literă mică, literă mare, cifră
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} disabled={loading} size="small" sx={{ fontSize: '0.8rem' }}>
          Anulează
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          size="small"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={14} /> : <LockResetIcon sx={{ fontSize: 16 }} />}
          sx={{ fontSize: '0.8rem' }}
        >
          Resetează
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// Componenta pentru afișarea parolei generate
interface PasswordResultDialogProps {
  open: boolean
  onClose: () => void
  password: string
  userEmail: string
}

const PasswordResultDialog: React.FC<PasswordResultDialogProps> = ({ 
  open, 
  onClose, 
  password,
  userEmail 
}) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(password)
    setCopied(true)
    toast.success('Parola a fost copiată în clipboard')
    setTimeout(() => setCopied(false), 3000)
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none' } }}
    >
      <DialogTitle sx={{ 
        py: 1.5, 
        px: 2,
        fontSize: '1rem',
        fontWeight: 600,
        borderBottom: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        color: 'success.main'
      }}>
        <LockResetIcon sx={{ fontSize: 20 }} />
        Parolă Resetată
      </DialogTitle>
      
      <DialogContent sx={{ p: 2 }}>
        <Box>
          <Typography sx={{ fontSize: '0.82rem', mb: 1.5 }} color="text.secondary">
            Utilizator: <strong>{userEmail}</strong>
          </Typography>
          
          <Typography sx={{ fontSize: '0.78rem', mb: 0.5 }} color="text.secondary">
            Noua parolă:
          </Typography>
          
          <Box 
            sx={{ 
              p: 1.5, 
              bgcolor: 'grey.900',
              borderRadius: 1, 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography 
              sx={{ 
                fontFamily: 'monospace', 
                fontSize: '1rem',
                fontWeight: 600,
                color: 'success.main'
              }}
            >
              {password}
            </Typography>
            <Tooltip title={copied ? 'Copiat!' : 'Copiază'}>
              <IconButton onClick={handleCopy} color="primary" size="small">
                <ContentCopyIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ 
            mt: 1.5, 
            p: 1, 
            bgcolor: 'warning.dark',
            color: 'warning.contrastText',
            borderRadius: 1,
            fontSize: '0.72rem'
          }}>
            Salvează această parolă - nu va mai fi afișată!
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} variant="contained" size="small" sx={{ fontSize: '0.8rem' }}>
          Am salvat parola
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// Pagina principală de administrare utilizatori
const AdminUsersPage: React.FC = () => {
  // const { user: currentUser } = useAuth() // Eliminăm variabila neutilizată
  const [users, setUsers] = useState<UserWithProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [totalUsers, setTotalUsers] = useState(0)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [filters, setFilters] = useState<UserFilters>({})
  const [searchTerm, setSearchTerm] = useState('')

  // State pentru dialog-uri
  const [userDialogOpen, setUserDialogOpen] = useState(false)
  const [scoringDialogOpen, setScoringDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<UserWithProfile | undefined>()
  const [selectedUserScore, setSelectedUserScore] = useState<any | undefined>() // UserScore type was removed, so using 'any' for now
  const [dialogLoading, setDialogLoading] = useState(false)
  
  // State pentru resetare parolă
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false)
  const [passwordResultDialogOpen, setPasswordResultDialogOpen] = useState(false)
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false)
  const [generatedPassword, setGeneratedPassword] = useState('')
  const [resetUserEmail, setResetUserEmail] = useState('')

  // Încarcă utilizatorii
  const loadUsers = async () => {
    try {
      setLoading(true)
      // console.log('Încărcare utilizatori...')
      // console.log('Token:', localStorage.getItem('accessToken'))
      
      const response = await userService.getUsers({
        ...filters,
        q: searchTerm,
        skip: page * rowsPerPage,
        limit: rowsPerPage
      })
      
      // console.log('Răspuns API:', response)
      setUsers(response.users)
      setTotalUsers(response.total)
    } catch (error: any) {
      console.error('Eroare la încărcarea utilizatorilor:', error)
      console.error('Detalii eroare:', error.response?.data)
      toast.error('Eroare la încărcarea utilizatorilor')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadUsers()
  }, [page, rowsPerPage, filters, searchTerm])

  // Gestionează crearea/editarea utilizatorului
  const handleSaveUser = async (userData: any) => {
    try {
      setDialogLoading(true)
      
      // console.log('handleSaveUser - userData primit:', userData)
      
      // Extrage datele de profil și instituțiile
      const { profile, institutions, coordinated_institutions, ...baseUserData } = userData
      
      // console.log('handleSaveUser - profile extrage:', profile)
      // console.log('handleSaveUser - baseUserData extrage:', baseUserData)
      
      if (selectedUser) {
        // Editare utilizator existent
        await userService.updateUser(selectedUser.id, baseUserData as UpdateUserRequest)
        
        // Actualizează profilul dacă există
        if (profile) {
          if (baseUserData.role === 'medic') {
            await userService.updateUserProfile(selectedUser.id, {
              first_name: profile.first_name,
              last_name: profile.last_name,
              cod_parafa: profile.cod_parafa,
              modalities: profile.modalities
            })
          } else if (baseUserData.role === 'operator') {
            await userService.updateUserProfile(selectedUser.id, {
              first_name: profile.first_name,
              last_name: profile.last_name,
              phone: profile.phone
            })
          } else if (baseUserData.role === 'tehnician') {
            await userService.updateUserProfile(selectedUser.id, {
              first_name: profile.first_name,
              last_name: profile.last_name,
              phone: profile.phone
            })
          } else if (baseUserData.role === 'admin') {
            // console.log('handleSaveUser - actualizez profil admin pentru:', selectedUser.id)
            // console.log('handleSaveUser - date profil admin:', {
            //   first_name: profile.first_name,
            //   last_name: profile.last_name,
            //   phone: profile.phone
            // })
            await userService.updateUserProfile(selectedUser.id, {
              first_name: profile.first_name,
              last_name: profile.last_name,
              phone: profile.phone
            })
            // console.log('handleSaveUser - profil admin actualizat cu succes')
          }
        }
        
        // Actualizează instituțiile utilizatorului
        if (institutions && Array.isArray(institutions)) {
          await userService.updateUserInstitutions(selectedUser.id, institutions)
        }
        
        // ✨ Actualizează instituțiile coordonate (doar pentru medici)
        if (baseUserData.role === 'medic' && coordinated_institutions !== undefined) {
          try {
            await fetch(`/api/v1/users/${selectedUser.id}/coordinated-institutions`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${getAccessToken()}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ institution_ids: coordinated_institutions })
            })
          } catch (coordError) {
            console.error('Eroare la actualizare coordonator:', coordError)
          }
        }
        
        toast.success('Utilizator actualizat cu succes')
      } else {
        // Creare utilizator nou
        const newUser = await userService.createUser(baseUserData as CreateUserRequest)
        
        // Creează profilul dacă există
        if (profile) {
          if (baseUserData.role === 'medic') {
            await userService.updateUserProfile(newUser.id, {
              first_name: profile.first_name,
              last_name: profile.last_name,
              cod_parafa: profile.cod_parafa,
              modalities: profile.modalities
            })
          } else if (baseUserData.role === 'operator') {
            await userService.updateUserProfile(newUser.id, {
              first_name: profile.first_name,
              last_name: profile.last_name,
              phone: profile.phone
            })
          } else if (baseUserData.role === 'tehnician') {
            await userService.updateUserProfile(newUser.id, {
              first_name: profile.first_name,
              last_name: profile.last_name,
              phone: profile.phone
            })
          } else if (baseUserData.role === 'admin') {
            await userService.updateUserProfile(newUser.id, {
              first_name: profile.first_name,
              last_name: profile.last_name,
              phone: profile.phone
            })
          }
        }
        
        // Setează instituțiile pentru utilizatorul nou
        if (institutions && Array.isArray(institutions) && institutions.length > 0) {
          await userService.updateUserInstitutions(newUser.id, institutions)
        }
        
        // ✨ Setează instituțiile coordonate pentru utilizatorul nou (doar medici)
        if (baseUserData.role === 'medic' && coordinated_institutions && coordinated_institutions.length > 0) {
          try {
            await fetch(`/api/v1/users/${newUser.id}/coordinated-institutions`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${getAccessToken()}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ institution_ids: coordinated_institutions })
            })
          } catch (coordError) {
            console.error('Eroare la setare coordonator:', coordError)
          }
        }
        
        toast.success('Utilizator creat cu succes')
      }
      
      setUserDialogOpen(false)
      setSelectedUser(undefined)
      loadUsers()
    } catch (error) {
      console.error('Eroare la salvare:', error)
      toast.error('Eroare la salvare')
    } finally {
      setDialogLoading(false)
    }
  }

  // Gestionează ștergerea utilizatorului
  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Sigur doriți să ștergeți acest utilizator?')) return

    try {
      await userService.deleteUser(userId)
      toast.success('Utilizator șters cu succes')
      loadUsers()
    } catch (error) {
      console.error('Eroare la ștergere:', error)
      toast.error('Eroare la ștergere')
    }
  }

  // Gestionează activarea/dezactivarea utilizatorului
  const handleToggleUserStatus = async (userId: string, active: boolean) => {
    try {
      await userService.updateUserStatus(userId, active)
      toast.success(`Utilizator ${active ? 'activat' : 'dezactivat'} cu succes`)
      loadUsers()
    } catch (error) {
      console.error('Eroare la schimbarea statusului:', error)
      toast.error('Eroare la schimbarea statusului')
    }
  }

  // Gestionează deschiderea dialog-ului de resetare parolă
  const handleOpenResetPassword = (user: UserWithProfile) => {
    setSelectedUser(user)
    setResetPasswordDialogOpen(true)
  }

  // Gestionează resetarea parolei
  const handleResetPassword = async (userId: string, autoGenerate: boolean, manualPassword?: string) => {
    try {
      setResetPasswordLoading(true)
      
      const response = await userService.adminResetPassword(userId, {
        auto_generate: autoGenerate,
        new_password: manualPassword
      })
      
      // Salvează parola și email-ul pentru afișare
      setGeneratedPassword(response.new_password)
      setResetUserEmail(response.user_email)
      
      // Închide dialog-ul de resetare și deschide dialog-ul cu rezultatul
      setResetPasswordDialogOpen(false)
      setPasswordResultDialogOpen(true)
      
      toast.success('Parolă resetată cu succes!')
    } catch (error: any) {
      console.error('Eroare la resetarea parolei:', error)
      toast.error(error.response?.data?.detail || 'Eroare la resetarea parolei')
    } finally {
      setResetPasswordLoading(false)
    }
  }

  // Gestionează deschiderea dialog-ului de scoring
  const handleOpenScoring = async (user: UserWithProfile) => {
    try {
      setSelectedUser(user)
      setScoringDialogOpen(true)
      setDialogLoading(true)
      
      const score = await userService.getUserScore(user.id)
      setSelectedUserScore(score)
    } catch (error) {
      console.error('Eroare la încărcarea scorului:', error)
      toast.error('Eroare la încărcarea scorului')
    } finally {
      setDialogLoading(false)
    }
  }

  // Filtre pentru roluri
  const roleFilters = [
    { value: '', label: 'Toate rolurile' },
    { value: 'admin', label: 'Administratori' },
    { value: 'operator', label: 'Operatori' },
    { value: 'medic', label: 'Medici' },
    { value: 'tehnician', label: 'Tehnicieni' }
  ]

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'error'
      case 'operator': return 'warning'
      case 'medic': return 'primary'
      case 'tehnician': return 'info'
      default: return 'default'
    }
  }

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin': return 'Administrator'
      case 'operator': return 'Operator'
      case 'medic': return 'Medic'
      case 'tehnician': return 'Tehnician'
      default: return role
    }
  }

  // Styling compact pentru input-uri
  const inputSx = { 
    '& .MuiInputBase-root': { fontSize: '0.75rem', height: 28 }, 
    '& .MuiInputLabel-root': { fontSize: '0.7rem' }
  }

  return (
    <Box sx={{ p: 1.5 }}>
      {/* Filtre și căutare - compact inline */}
      <Paper sx={{ p: 1.5, mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Căutare"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 0.5, color: 'text.secondary', fontSize: 16 }} />
            }}
            placeholder="Caută după nume, email..."
            sx={{ minWidth: 200, ...inputSx }}
          />
          
          <FormControl size="small" sx={{ minWidth: 120, ...inputSx }}>
            <InputLabel>Rol</InputLabel>
            <Select
              value={filters.role || ''}
              onChange={(e) => setFilters({ ...filters, role: e.target.value || undefined })}
              label="Rol"
            >
              {roleFilters.map((filter) => (
                <MenuItem key={filter.value} value={filter.value} sx={{ fontSize: '0.75rem' }}>
                  {filter.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 100, ...inputSx }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.active === undefined ? '' : filters.active.toString()}
              onChange={(e) => setFilters({ 
                ...filters, 
                active: e.target.value === '' ? undefined : e.target.value === 'true' 
              })}
              label="Status"
            >
              <MenuItem value="" sx={{ fontSize: '0.75rem' }}>Toate</MenuItem>
              <MenuItem value="true" sx={{ fontSize: '0.75rem' }}>Activi</MenuItem>
              <MenuItem value="false" sx={{ fontSize: '0.75rem' }}>Inactivi</MenuItem>
            </Select>
          </FormControl>

          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
            onClick={loadUsers}
            disabled={loading}
            sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none', px: 1.5 }}
          >
            Actualizează
          </Button>

          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            onClick={() => {
              setSelectedUser(undefined)
              setUserDialogOpen(true)
            }}
            sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none', px: 1.5, ml: 'auto' }}
          >
            Utilizator Nou
          </Button>
        </Box>
      </Paper>

      {/* Statistics Cards - compact */}
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ '& .MuiCardContent-root': { p: 1.5, '&:last-child': { pb: 1.5 } } }}>
            <CardContent>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 600 }} color="primary">
                {totalUsers}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem' }} color="text.secondary">
                Total utilizatori
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={6} sm={3}>
          <Card sx={{ '& .MuiCardContent-root': { p: 1.5, '&:last-child': { pb: 1.5 } } }}>
            <CardContent>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 600 }} color="success.main">
                {users ? users.filter(u => u.active).length : 0}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem' }} color="text.secondary">
                Utilizatori activi
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={6} sm={3}>
          <Card sx={{ '& .MuiCardContent-root': { p: 1.5, '&:last-child': { pb: 1.5 } } }}>
            <CardContent>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 600 }} color="primary">
                {users ? users.filter(u => u.role === 'medic').length : 0}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem' }} color="text.secondary">
                Medici
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={6} sm={3}>
          <Card sx={{ '& .MuiCardContent-root': { p: 1.5, '&:last-child': { pb: 1.5 } } }}>
            <CardContent>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 600 }} color="warning.main">
                {users ? users.filter(u => u.role === 'operator').length : 0}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem' }} color="text.secondary">
                Operatori
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabele cu utilizatori - compact cu dark header */}
      <Paper sx={{
        '& .MuiTypography-root': { fontSize: '0.84rem', lineHeight: 1.15 },
        '& .MuiChip-root': { height: 20, fontSize: '0.72rem' },
        '& .MuiTableCell-root': { py: 0.5, px: 1 },
        '& .MuiTableBody-root .MuiIconButton-root': { p: '3px' },
        '& .MuiTableBody-root .MuiSvgIcon-root': { fontSize: '1rem' }
      }}>
        <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{
                    bgcolor: 'grey.900',
                    '& th': {
                      color: 'common.white',
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      py: 0.75,
                      px: 1,
                      whiteSpace: 'nowrap'
                    }
                  }}>
                    <TableCell>Nume</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Rol</TableCell>
                    <TableCell>Tenant</TableCell>
                    <TableCell>Modalități</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Acțiuni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <CircularProgress size={20} />
                      </TableCell>
                    </TableRow>
                  ) : !users || users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        Nu s-au găsit utilizatori
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={0.75}>
                            <PersonIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
                              {user.first_name || user.doctor_profile?.first_name || ''} {user.last_name || user.doctor_profile?.last_name || ''}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.78rem' }} color="text.secondary">
                            {user.email}
                          </Typography>
                        </TableCell>
                        
                        <TableCell>
                          <Chip 
                            label={getRoleLabel(user.role)} 
                            color={getRoleColor(user.role) as any}
                            size="small"
                            sx={{ height: 20, fontSize: '0.72rem' }}
                          />
                        </TableCell>
                        
                        <TableCell>
                          <Typography sx={{ fontSize: '0.78rem' }} color="text.secondary">
                            {user.tenant_name || '-'}
                          </Typography>
                        </TableCell>
                        
                        <TableCell>
                          {user.doctor_profile?.modalities && (
                            <Box display="flex" gap={0.25} flexWrap="wrap">
                              {user.doctor_profile.modalities.slice(0, 3).map((modality) => (
                                <Chip key={modality} label={modality} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.68rem' }} />
                              ))}
                              {user.doctor_profile.modalities.length > 3 && (
                                <Chip label={`+${user.doctor_profile.modalities.length - 3}`} size="small" sx={{ height: 18, fontSize: '0.68rem' }} />
                              )}
                            </Box>
                          )}
                        </TableCell>
                        
                        <TableCell>
                          <Switch
                            checked={user.active}
                            onChange={(e) => handleToggleUserStatus(user.id, e.target.checked)}
                            color="success"
                            size="small"
                          />
                        </TableCell>
                        
                        <TableCell>
                          <Box display="flex" gap={0.25}>
                            <Tooltip title="Editează">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedUser(user)
                                  setUserDialogOpen(true)
                                }}
                                sx={{ p: 0.5 }}
                              >
                                <EditIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="Resetează parola">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenResetPassword(user)}
                                color="warning"
                                sx={{ p: 0.5 }}
                              >
                                <LockResetIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="Șterge">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteUser(user.id)}
                                color="error"
                                sx={{ p: 0.5 }}
                              >
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              component="div"
              count={totalUsers}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10))
                setPage(0)
              }}
              labelRowsPerPage="Rânduri pe pagină:"
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} din ${count !== -1 ? count : `mai mult de ${to}`}`
              }
            />
          </>
      </Paper>

      {/* Dialog-uri */}
      <UserDialog
        open={userDialogOpen}
        onClose={() => {
          setUserDialogOpen(false)
          setSelectedUser(undefined)
        }}
        user={selectedUser}
        onSave={handleSaveUser}
        loading={dialogLoading}
      />

      <ScoringDialog
        open={scoringDialogOpen}
        onClose={() => {
          setScoringDialogOpen(false)
          setSelectedUser(undefined)
          setSelectedUserScore(undefined)
        }}
        user={selectedUser}
        score={selectedUserScore}
        loading={dialogLoading}
      />

      <ResetPasswordDialog
        open={resetPasswordDialogOpen}
        onClose={() => {
          setResetPasswordDialogOpen(false)
          setSelectedUser(undefined)
        }}
        user={selectedUser}
        onReset={handleResetPassword}
        loading={resetPasswordLoading}
      />

      <PasswordResultDialog
        open={passwordResultDialogOpen}
        onClose={() => {
          setPasswordResultDialogOpen(false)
          setGeneratedPassword('')
          setResetUserEmail('')
        }}
        password={generatedPassword}
        userEmail={resetUserEmail}
      />
    </Box>
  )
}

export default AdminUsersPage
